/**
 * Frontend env validator + typed accessors.
 *
 * Validates required VITE_* vars at module load — throws on missing/empty.
 * Mirrors the BE validateEnv() / AI pydantic.from_env() fail-fast contract
 * locked in wiki/ledger/decisions/2026-05-14-env-naming-convention §3.
 *
 * Import once from main.tsx (or any module that loads at app start) so
 * validation runs at boot rather than on the first runtime read.
 */

interface Env {
  API_BASE_URL: string;
  AI_API_URL: string;
  AI_WS_URL: string;
  SEARCH_TRACE_ENABLED: boolean;
  FEATURE_FEEDBACK_ENABLED: boolean;
  FEATURE_CONFIDENCE_SCORE_UI: boolean;
  FEATURE_ANALYTICS_ENABLED: boolean;
}

const REQUIRED_KEYS = [
  "VITE_API_BASE_URL",
  "VITE_AI_API_URL",
  "VITE_AI_WS_URL",
] as const;

function readEnv(): Env {
  const raw = import.meta.env as unknown as Record<string, string | undefined>;
  const missing = REQUIRED_KEYS.filter((k) => !raw[k]);
  if (missing.length > 0) {
    throw new Error(
      `FE missing required env vars: ${missing.join(", ")}. ` +
        `Set them in .env.${raw.MODE ?? "<mode>"} for the build mode.`,
    );
  }

  return {
    API_BASE_URL: raw.VITE_API_BASE_URL!,
    AI_API_URL: raw.VITE_AI_API_URL!,
    AI_WS_URL: raw.VITE_AI_WS_URL!,
    SEARCH_TRACE_ENABLED: raw.VITE_SEARCH_TRACE_ENABLED === "true",
    FEATURE_FEEDBACK_ENABLED: raw.VITE_FEATURE_FEEDBACK_ENABLED === "true",
    // Confidence-score UI is held behind a flag pending legal release signoff.
    // Absent/non-"true" → off; do not enable without the legal gate cleared.
    FEATURE_CONFIDENCE_SCORE_UI:
      raw.VITE_FEATURE_CONFIDENCE_SCORE_UI === "true",
    // Ships dark; the BE mirrors this with FEATURE_ANALYTICS_ENABLED (503 when off).
    FEATURE_ANALYTICS_ENABLED: raw.VITE_FEATURE_ANALYTICS_ENABLED === "true",
  };
}

export const env: Env = readEnv();
